import { css, cx } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Callout, ExternalLink, ActionButtonType, Button, InlineEmptyStateOnboarding, tableBorder } from "@octopusdeploy/design-system-components";
import { fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, PlatformHubAccountResource, ProcessTemplateParameterResource, ProcessTemplateParameterValueResource, ScopeValues } from "@octopusdeploy/octopus-server-client";
import { AccountType, bindParameterName, ControlType, Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useMemo, useState } from "react";
import type { ProcessTemplateProducerActionDispatcher } from "~/analytics/Analytics";
import { Action } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import ProcessTemplateParameterEditorDialog from "~/areas/platformHub/processTemplates/ProcessTemplateParameterEditorDialog";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import type { ScopeSpecification } from "~/areas/variables/ReadonlyVariableResource/index";
import VariableScopeCell from "~/areas/variables/VariableScopeCell/index";
import { SortActionTemplateParametersButton } from "~/components/ActionTemplateParametersList/index";
import CheckboxInput from "~/components/ControlInputs/CheckboxInput";
import MultiLineTextInput from "~/components/ControlInputs/MultiLineTextInput";
import SelectInput from "~/components/ControlInputs/SelectInput";
import SingleLineTextInput from "~/components/ControlInputs/SingleLineTextInput";
import { controlTypeValueToTextMap } from "~/components/ControlType/ControlTypeSelector";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import DeleteDialog from "~/components/Dialog/DeleteDialog";
import DialogOpener from "~/components/Dialog/DialogOpener";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import Markdown from "~/components/Markdown";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import PermissionCheck, { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import { Select, Text } from "~/components/form/index";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import { DebounceText } from "~/primitiveComponents/form/Text/Text";
interface ProcessTemplateParametersTabProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
    dispatchProcessTemplateAction: ProcessTemplateProducerActionDispatcher;
    processTemplateLink: string | undefined;
}
export function ProcessTemplateParametersTab({ parameters, onChange, dispatchProcessTemplateAction, processTemplateLink }: ProcessTemplateParametersTabProps) {
    const { result: environments } = useQuery((repository) => repository.PlatformHub.getEnvironmentSummaries(), [], "Get environment summaries");
    const { result: platformHubAccounts } = useQuery((repository) => repository.PlatformHubAccounts.get({ Take: 1000000 }), [], "Get all platform hub accounts");
    const availableScopeValues = useMemo(() => ({ ...defaultScopeValues, Environments: environments?.map((e) => ({ Id: `${e.SpaceId}/${e.Id}`, Name: `${e.Name} (${e.SpaceName})` })) ?? [] }), [environments]);
    const onAdd = (parameter: ActionTemplateParameterResource) => {
        onChange([...parameters, parameter]);
        dispatchProcessTemplateAction("Process Template Parameter Created", { type: "Process Template Parameter Created", action: Action.Add, controlType: parameter.DisplaySettings["Octopus.ControlType"] });
    };
    if (parameters.length === 0) {
        return (<div className={styles.emptyStateContainer}>
                <ParametersOnboarding onAdd={onAdd} parameters={parameters}/>
            </div>);
    }
    if (!platformHubAccounts || !environments) {
        return null;
    }
    return (<>
            <div className={styles.headerContainer}>
                <OpenReorderParametersButton parameters={parameters} onChange={onChange}/>
                <AddProcessTemplateParameterButton parameters={parameters} onAdd={onAdd} type={ActionButtonType.Secondary}/>
            </div>
            <ProcessTemplateParametersTable parameters={parameters} onChange={onChange} availableScopeValues={availableScopeValues} platformHubAccounts={platformHubAccounts.Items} processTemplateLink={processTemplateLink}/>
        </>);
}
export type ParameterValueRow = {
    Parameter: ProcessTemplateParameterResource;
    Value: ProcessTemplateParameterValueResource | null;
    ParameterIndex: number;
    ValueIndex: number;
};
interface ProcessTemplateParametersTableProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountResource[];
    processTemplateLink: string | undefined;
}
function ProcessTemplateParametersTable({ parameters, onChange, availableScopeValues, platformHubAccounts, processTemplateLink }: ProcessTemplateParametersTableProps) {
    const [parameterToEdit, setParameterToEdit] = React.useState<ProcessTemplateParameterResource | null>(null);
    const [parameterToEditIndex, setParameterToEditIndex] = React.useState<number | null>(null);
    const [parameterToDelete, setParameterToDelete] = React.useState<{
        parameter: ProcessTemplateParameterResource;
        index: number;
    } | null>(null);
    const [focusedValueKey, setFocusedValueKey] = useState<ProcessTemplateValueKey | null>(null);
    const [isEditingScope, setIsEditingScope] = useState(false);
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const setEditingParameter = (parameter: ProcessTemplateParameterResource | null) => {
        setParameterToEdit(parameter);
        setParameterToEditIndex(parameter ? parameters.indexOf(parameter) : null);
    };
    const onParameterChange = (newParameter: ProcessTemplateParameterResource, parameterIndex: number) => {
        onChange(parameters.map((p, i) => (i === parameterIndex ? newParameter : p)));
    };
    const deleteParameter = (parameterIndex: number) => {
        const parameter = parameters[parameterIndex];
        setParameterToDelete({ parameter, index: parameterIndex });
    };
    const confirmDeleteParameter = () => {
        if (parameterToDelete) {
            onChange(parameters.filter((_, i) => i !== parameterToDelete.index));
            setParameterToDelete(null);
        }
    };
    const deleteValue = (parameterIndex: number, valueIndex: number) => {
        onChange(parameters.map((p, i) => {
            if (i === parameterIndex) {
                return {
                    ...p,
                    Values: p.Values.filter((_, j) => j !== valueIndex),
                };
            }
            return p;
        }));
    };
    const parameterValueRows: ParameterValueRow[] = parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        return p.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: p, Value: v, ParameterIndex: parameterIndex, ValueIndex: valueIndex })).concat([
            { Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: p.Values.length },
        ]);
    });
    const isParameterValueRowDisplayed = (row: ParameterValueRow) => {
        const controlType = row.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        // Hide the row with the "Add value" button for parameters that are not the account control type
        if (!isAccountControlType(controlType) && row.ValueIndex !== 0 && !row.Value) {
            return false;
        }
        return true;
    };
    return (<div>
            <DialogOpener open={!!parameterToEdit} onClose={() => setEditingParameter(null)}>
                {parameterToEdit ? (<ProcessTemplateParameterEditorDialog allParameters={parameters} parameter={parameterToEdit} onOk={(parameter) => onChange(parameters.map((p, i) => (i === parameterToEditIndex ? parameter : p)))} processTemplateLink={processTemplateLink}/>) : null}
            </DialogOpener>
            <DeleteDialog open={!!parameterToDelete} title={`Delete ${parameterToDelete?.parameter.Name ? `"${parameterToDelete.parameter.Name}"` : "Parameter"}?`} renderContent={() => (<Callout type={"warning"} title={"Potential data loss"}>
                        <div>
                            <p>If you delete a parameter, you'll lose any data associated with it in existing steps. You should ensure all usages are removed.</p>
                            {processTemplateLink && (<p>
                                    We recommend deleting parameters via the <ExternalLink label={"template's source code"} href={processTemplateLink}/>.
                                </p>)}
                        </div>
                    </Callout>)} onDeleteClick={async () => confirmDeleteParameter()} onClose={() => setParameterToDelete(null)}/>
            <GroupedDataTable data={parameterValueRows.filter(isParameterValueRowDisplayed)} columns={[
            {
                title: "Name",
                render: (data) => (data.ValueIndex === 0 ? <ProcessTemplateParameterNameCell data={data} onOpenEditParameterDialog={setEditingParameter} onDeleteParameter={deleteParameter}/> : null),
                width: "22.86%",
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Control Type",
                render: (data) => {
                    if (data.ValueIndex !== 0) {
                        return null;
                    }
                    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                    return <div>{controlTypeValueToTextMap.get(controlType) ?? controlType}</div>;
                },
                width: "15.24%",
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<ProcessTemplateParameterValueCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} valueIndex={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} platformHubAccounts={platformHubAccounts}/>),
                width: "30.95%",
                padding: "0",
            },
            {
                title: "Scope",
                render: (data) => {
                    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                    const getScopeCellContents = () => {
                        if (isControlTypeWithConfigurableScope(controlType) || (data.Value?.Scope.Environment && data.Value.Scope.Environment.length > 0)) {
                            return data.Value ? (<ProcessTemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={(parameter) => onParameterChange(parameter, data.ParameterIndex)} index={data.ValueIndex} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditing={isEditingScope} setIsEditing={setIsEditingScope} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={false}/>) : null;
                        }
                        return data.ValueIndex === 0 ? <div className={cx(styles.disabledText, styles.noScopeRequiredContainer)}>No scope required</div> : null;
                    };
                    return (<div className={cx(styles.parameterValueAndScopeContainer, { [styles.parameterValueAndScopeTallerContainer]: showMultipleValuesAndScopesIndicators(controlType, data.ValueIndex, data.Value, data.Parameter) })}>
                                    {showMultipleValuesAndScopesIndicators(controlType, data.ValueIndex, data.Value, data.Parameter) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                            <div>Multiple scopes</div>
                                        </div>)}
                                    <div className={styles.scopeAndOverflowMenuContainer}>
                                        <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                        {data.Value || isControlTypeSuppliedByProject(controlType) || (!data.Value && data.ValueIndex === 0) ? (<OverflowMenu menuItems={[
                                data.Value
                                    ? OverflowMenuItems.item("Delete value", async () => deleteValue(data.ParameterIndex, data.ValueIndex), {
                                        permission: Permission.PlatformHubEdit,
                                    })
                                    : null,
                            ]}/>) : null}
                                    </div>
                                </div>);
                },
                width: "30.95%",
                padding: "0",
                ref: scopeHeaderRef,
            },
        ]} getRowKey={(data) => `${data.ParameterIndex}-${data.ValueIndex}`}/>
        </div>);
}
interface ProcessTemplateParameterNameCellProps {
    data: ParameterValueRow;
    onOpenEditParameterDialog: (parameter: ProcessTemplateParameterResource) => void;
    onDeleteParameter: (parameterIndex: number) => void;
}
function ProcessTemplateParameterNameCell({ data, onOpenEditParameterDialog, onDeleteParameter }: ProcessTemplateParameterNameCellProps) {
    const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
    return (<div>
            <div className={styles.parameterNameContainer}>
                <div className={styles.parameterNameCellPrimaryHeader}>
                    <a href="" onClick={(e) => {
            e.preventDefault();
            onOpenEditParameterDialog(data.Parameter);
        }}>
                        <div>{data.Parameter.Name}</div>
                    </a>
                </div>
                <div className={cx("overflow-menu", { [styles.parameterNameCellOverflowMenuHidden]: !isOverflowMenuOpen, [styles.parameterNameCellOverflowMenuVisible]: isOverflowMenuOpen })}>
                    <OverflowMenu menuItems={[
            OverflowMenuItems.item("Edit parameter", () => onOpenEditParameterDialog(data.Parameter), { permission: Permission.PlatformHubEdit }),
            OverflowMenuItems.item("Delete parameter", () => onDeleteParameter(data.ParameterIndex), { permission: Permission.PlatformHubEdit }),
        ]} iconOrientation="horizontal" iconSize="small" onMenuStateChange={(menuState) => setIsOverflowMenuOpen(menuState.isOpen)}/>
                </div>
            </div>
            {data.Parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{data.Parameter.Label}</div>}
            <div className={styles.parameterHelpText}>
                <Markdown markup={data.Parameter.HelpText} noMargins={true} mutedTextColor={true}/>
            </div>
        </div>);
}
interface ProcessTemplateParameterValueCellProps {
    parameter: ProcessTemplateParameterResource;
    value: ProcessTemplateParameterValueResource | null;
    onChange: (parameter: ProcessTemplateParameterResource) => void;
    platformHubAccounts: PlatformHubAccountResource[];
    focusedValueKey: ProcessTemplateValueKey | null;
    setFocusedValueKey: (key: ProcessTemplateValueKey | null) => void;
    valueIndex: number;
}
function ProcessTemplateParameterValueCell({ parameter, value, onChange, platformHubAccounts, focusedValueKey, setFocusedValueKey, valueIndex }: ProcessTemplateParameterValueCellProps) {
    const { doBusyTask } = useLegacyDoBusyTask();
    const onValueChange = (newParameter: ProcessTemplateParameterResource) => {
        onChange(newParameter);
    };
    const addValue = () => {
        onChange({ ...parameter, Values: [...parameter.Values, { Value: "", Scope: {} }] });
    };
    const valueKey = createProcessTemplateValueKey(parameter.Name, valueIndex, "Value");
    const onFocus = () => setFocusedValueKey(valueKey);
    const onBlur = () => setFocusedValueKey(null);
    const focused = focusedValueKey === valueKey;
    const controlType = parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    if (!value) {
        const controlType = parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        // Disable the "Add value" button for control types that are supplied by the project
        if (isControlTypeSuppliedByProject(controlType)) {
            return <div className={cx(styles.disabledText, styles.valueContainer)}>Supplied by project</div>;
        }
        return (<div className={styles.valueContainer}>
                <Button label={valueIndex === 0 ? "Add value (optional)" : "Add another value"} onClick={() => addValue()} importance={"quiet"}/>
            </div>);
    }
    return (<div className={cx(styles.parameterValueAndScopeContainer, { [styles.parameterValueAndScopeTallerContainer]: showMultipleValuesAndScopesIndicators(controlType, valueIndex, value, parameter) })}>
            {showMultipleValuesAndScopesIndicators(controlType, valueIndex, value, parameter) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                </div>)}
            <div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
                <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} position={{ top: 0, left: -12, right: 0 }} isContentCentered={true}>
                    {focused ? (<div className={styles.popoverContainer}>
                            <ProcessTemplateParameterValueInput parameter={parameter} accounts={platformHubAccounts} localNames={[]} doBusyTask={doBusyTask} value={value.Value} onChange={(value) => onValueChange({ ...parameter, Values: parameter.Values.map((v, i) => (i === valueIndex ? { ...v, Value: value ?? "" } : v)) })} type={parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText} autoFocus={true}/>
                        </div>) : value.Value ? (<ReadonlyInlineVariableValue value={value.Value} variableType={getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {})} certificates={{}} workerPools={{}} hasEditPermissions={hasPermission(Permission.PlatformHubEdit)} sourceItems={undefined}/>) : (<Text value="" placeholder={"Enter value"} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>)}
                </PopoverWhenFocused>
            </div>
        </div>);
}
interface ProcessTemplateParameterValueInputProps {
    type: ControlType;
    value: string;
    parameter: ProcessTemplateParameterResource;
    localNames: string[];
    doBusyTask: DoBusyTask;
    accounts: PlatformHubAccountResource[];
    onChange: (value: string | undefined) => void;
    autoFocus?: boolean;
}
function ProcessTemplateParameterValueInput({ doBusyTask, localNames, accounts, type, value, parameter, onChange, autoFocus }: ProcessTemplateParameterValueInputProps) {
    switch (type) {
        case ControlType.SingleLineText:
            return <SingleLineTextInput label={""} onChange={onChange} value={value} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.MultiLineText:
            return <MultiLineTextInput label={""} onChange={onChange} value={value} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} monoSpacedFont={true} autoFocus={autoFocus}/>;
        case ControlType.Select:
            const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
            return <SelectInput label={""} onChange={onChange} value={value} resetValue={""} options={options} localNames={localNames} warning={undefined} allowClear={false} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>;
        case ControlType.Checkbox:
            return <CheckboxInput label={""} onChange={onChange} value={value} resetValue={""} localNames={localNames} warning={undefined} defaultValueIndicator={undefined} doBusyTask={doBusyTask}/>;
        case ControlType.AmazonWebServicesAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.AmazonWebServicesAccount, AccountType.AmazonWebServicesOidcAccount]}/>;
        case ControlType.AzureAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc]}/>;
        case ControlType.GoogleCloudAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.GoogleCloudAccount]}/>;
        case ControlType.UsernamePasswordAccount:
            return <PlatformHubAccountSelect onChange={onChange} value={value} accounts={accounts} type={[AccountType.UsernamePassword]}/>;
        case ControlType.Feed:
            throw new Error("Feed values are currently not supported in process templates");
        case ControlType.Sensitive:
            throw new Error("Sensitive values are not supported in process templates");
        case ControlType.Certificate:
            throw new Error("Certificate values are currently not supported in process templates");
        case ControlType.WorkerPool:
            throw new Error("Worker pool values are currently not supported in process templates");
        case ControlType.Package:
            throw new Error("Package values are currently not supported in process templates");
        case ControlType.StepName:
            throw new Error("Step name values are currently not supported in process templates");
        case ControlType.TargetTags:
            throw new Error("Target tag values are currently not supported in process templates");
        case ControlType.Environments:
            throw new Error("Environment values are currently not supported in process templates");
        case ControlType.TenantTags:
            throw new Error("Tenant tag values are currently not supported in process templates");
        case ControlType.Teams:
            throw new Error("Teams values are currently not supported in process templates");
        case ControlType.Channels:
            throw new Error("Channel values are currently not supported in process templates");
        case ControlType.Project:
            throw new Error("Project values are currently not supported in process templates");
        default:
            return (<React.Fragment>
                    <DebounceText value={value} warning={undefined} onChange={onChange}/>
                </React.Fragment>);
    }
}
interface PlatformHubAccountSelectProps {
    onChange: (value: string | undefined) => void;
    value: string | undefined;
    accounts: PlatformHubAccountResource[];
    type: AccountType[];
}
function PlatformHubAccountSelect({ onChange, value, accounts, type }: PlatformHubAccountSelectProps) {
    const accountItems = accounts.filter((a) => type.includes(a.AccountType)).map((a) => ({ value: a.Id, text: a.Name }));
    return (<div className={styles.accountSelectContainer}>
            <Select onChange={onChange} value={value} items={accountItems}/>
        </div>);
}
interface ProcessTemplateParameterScopeCellProps {
    parameter: ProcessTemplateParameterResource;
    value: ProcessTemplateParameterValueResource;
    onChange: (parameter: ProcessTemplateParameterResource) => void;
    focusedValueKey: ProcessTemplateValueKey | null;
    setFocusedValueKey: (key: ProcessTemplateValueKey | null) => void;
    isEditing: boolean;
    setIsEditing?: (isEditing: boolean) => void;
    index: number;
    scopeValues: ScopeValues;
    height: number;
    width: number;
    isConsumer: boolean;
}
export function ProcessTemplateParameterScopeCell({ parameter, value, onChange, focusedValueKey, setFocusedValueKey, isEditing, setIsEditing, index, scopeValues, height, width, isConsumer }: ProcessTemplateParameterScopeCellProps) {
    const { doBusyTask } = useLegacyDoBusyTask();
    const onScopeChange = (newScope: ScopeSpecification, valueIndex: number) => {
        const newParameter = {
            ...parameter,
            Values: parameter.Values.map((v, i) => i === valueIndex
                ? {
                    ...v,
                    Scope: {
                        Environment: newScope.Environment ? [...newScope.Environment] : undefined,
                    },
                }
                : v),
        };
        onChange(newParameter);
    };
    const variableType = getVariableTypeFromDisplaySettings(parameter.DisplaySettings["Octopus.ControlType"]);
    const valueKey = createProcessTemplateValueKey(parameter.Name, index, "Scope");
    const noScopes = Object.values(value.Scope).reduce((acc, scope) => acc + (scope?.length ?? 0), 0) === 0;
    if (isConsumer && noScopes) {
        return null;
    }
    const readOnly = isConsumer;
    return (<VariableScopeCell scope={value.Scope} onScopeChanged={(scope) => onScopeChange(scope, index)} availableScopes={scopeValues} excludedScopes={value.OverridenScope} tagIndex={{}} environmentsById={{}} variableType={variableType} isEditing={valueKey === focusedValueKey && isEditing} isViewingAll={valueKey === focusedValueKey} doBusyTask={doBusyTask} onFocusEdit={() => {
            setFocusedValueKey(valueKey);
            setIsEditing && setIsEditing(true);
        }} onBlurEdit={() => {
            setFocusedValueKey(null);
            setIsEditing && setIsEditing(false);
        }} onFocusViewAll={() => setFocusedValueKey(valueKey)} onBlurViewAll={() => setFocusedValueKey(null)} onOpenEditorClicked={() => setIsEditing && setIsEditing(true)} cellHeight={height} containerWidth={width} isFullWidth={true} editorScope={"PlatformHub"} deleted={readOnly} isEnvironmentExcludedDescription={value.OverrideValue ? `Overridden by ${bindParameterName(value.OverrideValue)}` : undefined}/>);
}
export const baseCellHeightInRem = 3;
export const multipleValuesAndScopesIndicatorHeightInRem = 1.75;
const styles = {
    emptyStateContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    }),
    headerContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        gap: space[12],
        paddingRight: space[16],
        paddingTop: space[24],
        paddingBottom: space[24],
    }),
    parameterNameContainer: css({
        display: "flex",
        width: "100%",
        alignItems: "center",
        "&:hover": {
            ".overflow-menu": {
                visibility: "visible",
            },
        },
    }),
    parameterNameCellPrimaryHeader: css({
        font: text.code.regular.medium,
        fontWeight: fontWeight["700"],
        overflowWrap: "anywhere",
        hyphens: "auto",
        color: themeTokens.color.text.link.default,
    }),
    parameterNameCellOverflowMenuVisible: css({ visibility: "visible" }),
    parameterNameCellOverflowMenuHidden: css({ visibility: "hidden" }),
    parameterNameCellSecondaryHeader: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterValueAndScopeContainer: css({
        height: `${baseCellHeightInRem}rem`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1",
    }),
    parameterValueAndScopeTallerContainer: css({
        height: `${baseCellHeightInRem + multipleValuesAndScopesIndicatorHeightInRem}rem`,
    }),
    parameterValues: css({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    parameterHelpText: css({
        font: text.body.regular.small,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    disabledText: css({
        color: themeTokens.color.text.disabled,
    }),
    noScopeRequiredContainer: css({
        marginLeft: "1.25rem",
        marginRight: "1.25rem",
    }),
    scopeAndOverflowMenuContainer: css({
        display: "flex",
        width: "100%",
        gap: space[4],
        alignItems: "center",
        height: `${baseCellHeightInRem}rem`,
    }),
    valueContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: `${baseCellHeightInRem}rem`,
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    scopeContainer: css({
        flexGrow: 1,
        height: "100%",
        display: "flex",
        alignItems: "center",
    }),
    popoverContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
    }),
    accountSelectContainer: css({
        height: "4rem",
    }),
    multipleValuesAndScopesIndicator: css({
        height: `${multipleValuesAndScopesIndicatorHeightInRem}rem`,
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        borderBottom: tableBorder,
        display: "flex",
        alignItems: "center",
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
};
interface AddProcessTemplateParameterButtonProps {
    parameters: ProcessTemplateParameterResource[];
    onAdd: (parameter: ActionTemplateParameterResource) => void;
    type: ActionButtonType;
}
export function AddProcessTemplateParameterButton({ parameters, onAdd, type }: AddProcessTemplateParameterButtonProps) {
    return (<PermissionCheck key="AddParameterPermission" {...parameterEditPermission}>
            <OpenDialogButton key="AddParameter" label={`Add parameter`} type={type}>
                <ProcessTemplateParameterEditorDialog allParameters={parameters} onOk={onAdd}/>
            </OpenDialogButton>
        </PermissionCheck>);
}
interface OpenReorderParametersButtonProps {
    parameters: ProcessTemplateParameterResource[];
    onChange: (parameters: ActionTemplateParameterResource[]) => void;
}
function OpenReorderParametersButton({ parameters, onChange }: OpenReorderParametersButtonProps) {
    return <SortActionTemplateParametersButton parameters={parameters} onChange={onChange} permission={parameterEditPermission} name={"parameter"}/>;
}
interface ParametersOnboardingProps {
    parameters: ProcessTemplateParameterResource[];
    onAdd: (parameter: ActionTemplateParameterResource) => void;
}
function ParametersOnboarding({ onAdd, parameters }: ParametersOnboardingProps) {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const addParameterAction: PrimaryPageAction = {
        type: "button",
        label: "Add Parameter",
        onClick: () => setIsDialogOpen(true),
        hasPermissions: isAllowed(parameterEditPermission),
    };
    return (<>
            <DialogOpener open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                <ProcessTemplateParameterEditorDialog allParameters={parameters} onOk={onAdd}/>
            </DialogOpener>
            <InlineEmptyStateOnboarding title="This template doesn’t have any parameters yet." description="Start creating by clicking on Add Parameter" illustration="NoEntities" actions={{ primary: addParameterAction }}/>
        </>);
}
const parameterEditPermission = {
    permission: Permission.PlatformHubEdit,
};
const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
export type ProcessTemplateValueKey = `${string};${string};${string}`;
export function createProcessTemplateValueKey(parameterName: string, index: number, type: "Value" | "Scope"): ProcessTemplateValueKey {
    return `${parameterName};${index};${type}`;
}
const isControlTypeSuppliedByProject = (controlType: ControlType): boolean => {
    return (controlType === ControlType.Package ||
        controlType === ControlType.Certificate ||
        controlType === ControlType.Sensitive ||
        controlType === ControlType.WorkerPool ||
        controlType === ControlType.StepName ||
        controlType === ControlType.TargetTags ||
        controlType === ControlType.Feed ||
        controlType === ControlType.Environments ||
        controlType === ControlType.TenantTags ||
        controlType === ControlType.Teams ||
        controlType === ControlType.Channels ||
        controlType === ControlType.Project);
};
export const isAccountControlType = (controlType: ControlType): boolean => {
    return (controlType === ControlType.AzureAccount ||
        controlType === ControlType.AmazonWebServicesAccount ||
        controlType === ControlType.GoogleCloudAccount ||
        controlType === ControlType.UsernamePasswordAccount ||
        controlType === ControlType.GenericOidcAccount);
};
export const isAccountControlTypeWithoutDefaultValues = (data: ParameterValueRow): boolean => {
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    return isAccountControlType(controlType) && !data.Value;
};
const isControlTypeWithConfigurableScope = (controlType: ControlType): boolean => {
    return isAccountControlType(controlType);
};
export const showMultipleValuesAndScopesIndicators = (controlType: ControlType, valueIndex: number, value: ProcessTemplateParameterValueResource | null, parameter: ProcessTemplateParameterResource): boolean => {
    const isAccountControlTypeHeaderRowWithAtLeastOneValue = isAccountControlType(controlType) && valueIndex === 0 && !!value;
    // Provided for backwards compatability, as non-account configurable parameters could previously have multiple values, but can now only have a single value.
    const isNonAccountConfigurableControlTypeHeaderRowWithMoreThanOneValue = !isAccountControlType(controlType) && !isControlTypeSuppliedByProject(controlType) && valueIndex === 0 && parameter.Values.length > 1;
    return isAccountControlTypeHeaderRowWithAtLeastOneValue || isNonAccountConfigurableControlTypeHeaderRowWithMoreThanOneValue;
};
